import { useState, useEffect, useCallback } from "react";
import { supabase } from "../lib/supabase";
import { useData } from "./useData";

// =============================================================================
// useOrgBackground Hook
// =============================================================================
// Manages per-organization background settings:
// - Fetches current setting from org_background_settings table
// - Handles preset selection (CSS gradients or local images)
// - Handles custom image upload to Supabase Storage
// - Returns the appropriate CSS class or inline style for the background
// =============================================================================

export type BackgroundType = "preset" | "custom" | "gradient";

export interface BackgroundSetting {
  id: string;
  organization_id: string;
  background_type: BackgroundType;
  preset_name: string | null;
  custom_url: string | null;
  created_at: string;
  updated_at: string;
}

// Solid color presets — CSS classes defined in index.css
export const GRADIENT_PRESETS = [
  { name: "offwhite", label: "Offwhite", type: "gradient" as const },
  { name: "charcoal", label: "Charcoal", type: "gradient" as const },
] as const;

// Available image presets — local files in public/backgrounds/
// User prefers: vibrant saturated colors, defined shapes with structure, NOT grey/muted/minimal
export const IMAGE_PRESETS = [
  // Purple & Pink - deep colors with flowing structure
  {
    name: "liquid-04",
    label: "Dark Purple",
    type: "preset" as const,
    file: "/backgrounds/liquid-04.png",
    category: "purple",
  },
  {
    name: "liquid-05",
    label: "Abstract Glow",
    type: "preset" as const,
    file: "/backgrounds/liquid-05.png",
    category: "purple",
  },
  // Mesh Gradients - vibrant blobs with defined edges
  {
    name: "mesh-01",
    label: "Neon Mesh",
    type: "preset" as const,
    file: "/backgrounds/mesh-01.jpg",
    category: "mesh",
  },
  {
    name: "mesh-02",
    label: "Cosmic Swirl",
    type: "preset" as const,
    file: "/backgrounds/mesh-02.jpg",
    category: "mesh",
  },
  {
    name: "mesh-03",
    label: "Colorful Flow",
    type: "preset" as const,
    file: "/backgrounds/mesh-03.jpg",
    category: "mesh",
  },
  {
    name: "mesh-04",
    label: "Teal Bloom",
    type: "preset" as const,
    file: "/backgrounds/mesh-04.jpg",
    category: "mesh",
  },
  // 3D Renders - vibrant abstract geometry
  {
    name: "render-01",
    label: "Neon Blobs",
    type: "preset" as const,
    file: "/backgrounds/render-01.jpg",
    category: "render",
  },
  {
    name: "render-02",
    label: "Blue Clean",
    type: "preset" as const,
    file: "/backgrounds/render-02.jpg",
    category: "render",
  },
  {
    name: "render-03",
    label: "Sunset Blend",
    type: "preset" as const,
    file: "/backgrounds/render-03.jpg",
    category: "render",
  },
  // Layered Waves - aurora and color bands
  {
    name: "layers-01",
    label: "Aurora Night",
    type: "preset" as const,
    file: "/backgrounds/layers-01.jpg",
    category: "layers",
  },
  {
    name: "layers-02",
    label: "Holographic",
    type: "preset" as const,
    file: "/backgrounds/layers-02.jpg",
    category: "layers",
  },
  {
    name: "layers-03",
    label: "Sunset Horizon",
    type: "preset" as const,
    file: "/backgrounds/layers-03.jpg",
    category: "layers",
  },
  {
    name: "layers-04",
    label: "Teal Pink",
    type: "preset" as const,
    file: "/backgrounds/layers-04.jpg",
    category: "layers",
  },
  // Space
  {
    name: "space-earth",
    label: "Earth Orbit",
    type: "preset" as const,
    file: "/backgrounds/space-earth.png",
    category: "space",
  },
  // Mountain landscapes (natural structure)
  {
    name: "gradient-01",
    label: "Mountain Clouds",
    type: "preset" as const,
    file: "/backgrounds/gradient-01.png",
    category: "landscape",
  },
  {
    name: "gradient-02",
    label: "Dramatic Peak",
    type: "preset" as const,
    file: "/backgrounds/gradient-02.png",
    category: "landscape",
  },
  {
    name: "gradient-03",
    label: "Snowy Night",
    type: "preset" as const,
    file: "/backgrounds/gradient-03.png",
    category: "landscape",
  },
  {
    name: "gradient-04",
    label: "Mountain Range",
    type: "preset" as const,
    file: "/backgrounds/gradient-04.png",
    category: "landscape",
  },
  {
    name: "gradient-05",
    label: "Misty Mountains",
    type: "preset" as const,
    file: "/backgrounds/gradient-05.png",
    category: "landscape",
  },
] as const;

// Helper to filter presets by category
export const getPresetsByCategory = (category: string) =>
  IMAGE_PRESETS.filter((p) => p.category === category);

export const LIQUID_GLASS_CATEGORIES = [
  { id: "purple", label: "Purple & Pink" },
  { id: "mesh", label: "Mesh Gradients" },
  { id: "render", label: "3D Renders" },
  { id: "layers", label: "Layered Waves" },
  { id: "space", label: "Space" },
] as const;

export const ALL_PRESETS = [...GRADIENT_PRESETS, ...IMAGE_PRESETS];

interface UseOrgBackgroundReturn {
  // Current state
  setting: BackgroundSetting | null;
  isLoading: boolean;
  error: string | null;

  // Computed styles
  backgroundStyle: React.CSSProperties;
  backgroundClass: string;

  // Actions
  selectPreset: (presetName: string, type: BackgroundType) => Promise<void>;
  uploadCustom: (file: File) => Promise<void>;
  refresh: () => Promise<void>;

  // Upload state
  isUploading: boolean;
  uploadProgress: number;
}

export function useOrgBackground(): UseOrgBackgroundReturn {
  const { currentOrganization } = useData();
  const organizationId = currentOrganization?.id;

  const [setting, setSetting] = useState<BackgroundSetting | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Fetch current setting
  const fetchSetting = useCallback(async () => {
    if (!organizationId || !supabase) {
      setIsLoading(false);
      return;
    }

    try {
      setError(null);
      const { data, error: fetchError } = await supabase
        .from("org_background_settings")
        .select("*")
        .eq("organization_id", organizationId)
        .maybeSingle();

      if (fetchError) throw fetchError;
      setSetting(data);
    } catch (err) {
      console.error("Failed to fetch background setting:", err);
      setError(err instanceof Error ? err.message : "Failed to load settings");
    } finally {
      setIsLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchSetting();
  }, [fetchSetting]);

  // Select a preset (gradient or image)
  const selectPreset = useCallback(
    async (presetName: string, type: BackgroundType) => {
      if (!organizationId || !supabase) return;

      try {
        setError(null);
        const payload = {
          organization_id: organizationId,
          background_type: type,
          preset_name: presetName,
          custom_url: null,
        };

        const { data, error: upsertError } = await supabase
          .from("org_background_settings")
          .upsert(payload, { onConflict: "organization_id" })
          .select()
          .single();

        if (upsertError) throw upsertError;
        setSetting(data);
      } catch (err) {
        console.error("Failed to save preset:", err);
        setError(err instanceof Error ? err.message : "Failed to save");
        throw err;
      }
    },
    [organizationId]
  );

  // Upload custom image
  const uploadCustom = useCallback(
    async (file: File) => {
      if (!organizationId || !supabase) return;

      // Validate file
      const validTypes = ["image/jpeg", "image/png", "image/webp"];
      if (!validTypes.includes(file.type)) {
        setError("Invalid file type. Use JPEG, PNG, or WebP.");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError("File too large. Maximum 5MB.");
        return;
      }

      try {
        setIsUploading(true);
        setUploadProgress(0);
        setError(null);

        // Generate unique filename
        const ext = file.name.split(".").pop() || "png";
        const filename = `${organizationId}/background-${Date.now()}.${ext}`;

        // Upload to Supabase Storage
        setUploadProgress(30);
        const { error: uploadError } = await supabase.storage
          .from("org-backgrounds")
          .upload(filename, file, {
            cacheControl: "3600",
            upsert: true,
          });

        if (uploadError) throw uploadError;
        setUploadProgress(70);

        // Get public URL
        const { data: urlData } = supabase.storage.from("org-backgrounds").getPublicUrl(filename);

        const publicUrl = urlData.publicUrl;

        // Save setting
        const { data, error: upsertError } = await supabase
          .from("org_background_settings")
          .upsert(
            {
              organization_id: organizationId,
              background_type: "custom" as BackgroundType,
              preset_name: null,
              custom_url: publicUrl,
            },
            { onConflict: "organization_id" }
          )
          .select()
          .single();

        if (upsertError) throw upsertError;
        setUploadProgress(100);
        setSetting(data);
      } catch (err) {
        console.error("Failed to upload:", err);
        setError(err instanceof Error ? err.message : "Upload failed");
        throw err;
      } finally {
        setIsUploading(false);
        setUploadProgress(0);
      }
    },
    [organizationId]
  );

  // Compute background style based on current setting
  const getBackgroundStyle = useCallback((): React.CSSProperties => {
    if (!setting) return {};

    if (setting.background_type === "custom" && setting.custom_url) {
      return {
        backgroundImage: `url(${setting.custom_url})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      };
    }

    if (setting.background_type === "preset" && setting.preset_name) {
      const preset = IMAGE_PRESETS.find((p) => p.name === setting.preset_name);
      if (preset) {
        return {
          backgroundImage: `url(${preset.file})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        };
      }
    }

    // Gradient presets use CSS classes, not inline styles
    return {};
  }, [setting]);

  // Get CSS class for gradient presets
  const getBackgroundClass = useCallback((): string => {
    // No gradient presets currently - all backgrounds use images
    if (!setting) return "";

    if (setting.background_type === "gradient" && setting.preset_name) {
      return `bg-${setting.preset_name}`;
    }

    // Custom or image presets don't use classes
    return "";
  }, [setting]);

  return {
    setting,
    isLoading,
    error,
    backgroundStyle: getBackgroundStyle(),
    backgroundClass: getBackgroundClass(),
    selectPreset,
    uploadCustom,
    refresh: fetchSetting,
    isUploading,
    uploadProgress,
  };
}

// =============================================================================
// Helper: Get background props for App.tsx
// =============================================================================
// Returns either a className (for gradients) or style object (for images)
// Default background when no setting exists
const DEFAULT_BACKGROUND = IMAGE_PRESETS[0]; // First available preset

export function getBackgroundProps(setting: BackgroundSetting | null): {
  className?: string;
  style?: React.CSSProperties;
} {
  // Default to first image preset
  if (!setting) {
    return {
      style: {
        backgroundImage: `url(${DEFAULT_BACKGROUND.file})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      },
    };
  }

  if (setting.background_type === "custom" && setting.custom_url) {
    return {
      style: {
        backgroundImage: `url(${setting.custom_url})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      },
    };
  }

  if (setting.background_type === "preset" && setting.preset_name) {
    const preset = IMAGE_PRESETS.find((p) => p.name === setting.preset_name);
    if (preset) {
      return {
        style: {
          backgroundImage: `url(${preset.file})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        },
      };
    }
  }

  if (setting.background_type === "gradient" && setting.preset_name) {
    return { className: `bg-${setting.preset_name}` };
  }

  // Fallback to first preset
  return {
    style: {
      backgroundImage: `url(${DEFAULT_BACKGROUND.file})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundAttachment: "fixed",
    },
  };
}
